import { useEffect, useRef, useState } from 'react';
import { Button, Paragraph, Text } from '@hungryroot/ramen';

import styles from './PromoCodeInput.module.css';

interface Props {
  label: string;
  error?: string;
  initialValue?: string;
  placeholder: string;
  actionText: string;
  onSubmit(value: string): void;
  onCancel(): void;
  autofocus?: boolean;
  loading?: boolean;
}

export function PromoCodeInput({
  initialValue = '',
  error,
  label,
  loading,
  onSubmit,
  onCancel,
  autofocus = true,
  placeholder,
  actionText,
}: Props) {
  const wrapperClassName = error !== undefined ? styles.error : styles.wrapper;
  const [inputValue, setInputValue] = useState<string>(initialValue);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Previously we were using onBlur to detect if the user clicked outside the component.
    // On iOS Safari, e.relatedTarget was null causing the Submit button to trigger the onCancel effect erronenously.
    // This workaround uses a useEffect to detect if the user clicks outside the component to trigger the onCancel
    // effect and should be more compatible with all browsers.

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onCancel();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onCancel]);

  return (
    <div className={wrapperClassName}>
      <div
        onKeyUp={(e) => {
          if (e.key === 'Escape') {
            onCancel();
          }

          if (e.key === 'Enter' && inputValue) {
            onSubmit(inputValue);
          }
        }}
        ref={containerRef}
        className={styles.container}
      >
        <label className={styles['label-wrapper']}>
          <Paragraph variant="caption" className={styles.label}>
            {label}
          </Paragraph>
          <Text className={styles['input-wrapper']}>
            <input
              autoFocus={autofocus}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
              }}
              placeholder={placeholder}
              type="text"
              className={styles.input}
            />
          </Text>
        </label>
        <Button
          onClick={() => {
            if (inputValue === undefined) {
              return;
            }

            onSubmit(inputValue);
          }}
          size="small"
          loading={loading}
          style="default"
          prominence="tertiary"
          textPrimary={actionText}
        />
      </div>
      {error !== undefined && (
        <Paragraph variant="caption" className={styles['error-text']}>
          {error}
        </Paragraph>
      )}
    </div>
  );
}
